import axios, { HttpStatusCode } from 'axios';
import httpClient from '@/src/lib/api/http/client';
import { ApplicationError } from '@/src/shared/errors';
import { safeParseZodObject } from '@/src/shared/utils/parser';
import { DGetSelfMedicationContact } from '../data/dtos/contact';
import {
  DCreateSelfMedicationConversation,
  DGetSelfMedicationConversation,
} from '../data/dtos/conversation';
import { DUpdateSelfMedicationLastSeen } from '../data/dtos/last-seen';
import {
  DCreateSelfMedicationMessage,
  DGetSelfMedicationMessages,
} from '../data/dtos/message';
import {
  mapChatContactSchemaToChatContactModel,
  mapChatConversationSchemaToChatConversationModel,
  mapChatMessageSchemaToChatMessageModel,
} from '../data/mappers';
import {
  SChatContactResponse,
  TChatContactResponse,
} from '../data/schemas/api/responses/chat-contact';
import {
  SChatConversationResponse,
  TChatConversationResponse,
} from '../data/schemas/api/responses/chat-conversation';
import {
  SChatMessageResponse,
  SChatMessagesResponse,
  TChatMessageResponse,
  TChatMessagesResponse,
} from '../data/schemas/api/responses/chat-message';
import {
  SUpdateLastSeenResponse,
  TUpdateLastSeenResponse,
} from '../data/schemas/api/responses/last-seen';
import {
  CreateConversationFailedError,
  GetContactFailedError,
  GetConversationsFailedError,
  GetMessagesFailedError,
  SendMessageFailedError,
  UpdateLastSeenFailedError,
} from '../errors';

export const selfMedicationChatService = {
  getUserConversation: async ({ userId }: DGetSelfMedicationConversation) => {
    try {
      const response = await httpClient.healthyoneMarketplace.get(
        `/chat/self-medication/users/${userId}/conversation`
      );

      const responseSchema = safeParseZodObject<TChatConversationResponse>(
        SChatConversationResponse,
        response.data
      );
      return mapChatConversationSchemaToChatConversationModel(
        responseSchema.data
      );
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (!error.response) {
          throw new GetConversationsFailedError();
        }
        if (error.response.status === HttpStatusCode.NotFound) {
          return null;
        }
        throw new ApplicationError('Error', error.response.data.error.message);
      }

      throw error;
    }
  },

  getUserContact: async ({ userId }: DGetSelfMedicationContact) => {
    try {
      const response = await httpClient.healthyoneMarketplace.get(
        `/chat/self-medication/users/${userId}/contact`
      );

      const responseSchema = safeParseZodObject<TChatContactResponse>(
        SChatContactResponse,
        response.data
      );
      return mapChatContactSchemaToChatContactModel(responseSchema.data);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (!error.response) {
          throw new GetContactFailedError();
        }
        throw new ApplicationError('Error', error.response.data.error.message);
      }

      throw error;
    }
  },

  getMessages: async ({
    userId,
    lastMessageId,
  }: DGetSelfMedicationMessages) => {
    const params = {
      ...(lastMessageId && { last_message_id: lastMessageId }),
    };

    try {
      const response = await httpClient.healthyoneMarketplace.get(
        `/chat/self-medication/users/${userId}/conversation/messages`,
        { params }
      );

      const responseSchema = safeParseZodObject<TChatMessagesResponse>(
        SChatMessagesResponse,
        response.data
      );
      return responseSchema.data.map((message) => {
        return mapChatMessageSchemaToChatMessageModel(message);
      });
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (!error.response) {
          throw new GetMessagesFailedError();
        }
        throw new ApplicationError('Error', error.response.data.error.message);
      }

      throw error;
    }
  },

  sendMessage: async ({
    userId,
    content,
    attachments,
  }: DCreateSelfMedicationMessage) => {
    const request = new FormData();
    if (content) {
      request.append('content', content);
    }
    if (attachments && attachments.length > 0) {
      attachments.forEach((file) => {
        request.append('attachments', file);
      });
    }

    try {
      const response = await httpClient.healthyoneMarketplace.post(
        `/chat/self-medication/users/${userId}/conversation/messages`,
        request
      );
      const responseSchema = safeParseZodObject<TChatMessageResponse>(
        SChatMessageResponse,
        response.data
      );
      return mapChatMessageSchemaToChatMessageModel(responseSchema.data);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (!error.response) {
          throw new SendMessageFailedError();
        }
        throw new ApplicationError('Error', error.response.data.error.message);
      }

      throw error;
    }
  },

  createConversation: async ({ userId }: DCreateSelfMedicationConversation) => {
    try {
      const response = await httpClient.healthyoneMarketplace.post(
        `/chat/self-medication/users/${userId}/conversation`
      );
      const responseSchema = safeParseZodObject<TChatConversationResponse>(
        SChatConversationResponse,
        response.data
      );
      return mapChatConversationSchemaToChatConversationModel(
        responseSchema.data
      );
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (!error.response) {
          throw new CreateConversationFailedError();
        }
        throw new ApplicationError('Error', error.response.data.error.message);
      }

      throw error;
    }
  },

  updateLastSeen: async ({ userId }: DUpdateSelfMedicationLastSeen) => {
    try {
      const response = await httpClient.healthyoneMarketplace.patch(
        `/chat/self-medication/users/${userId}/conversation/last-seen`
      );
      const responseSchema = safeParseZodObject<TUpdateLastSeenResponse>(
        SUpdateLastSeenResponse,
        response.data
      );
      return responseSchema.data.last_seen;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (!error.response) {
          throw new UpdateLastSeenFailedError();
        }
        throw new ApplicationError('Error', error.response.data.error.message);
      }

      throw error;
    }
  },
};
